import { FloorplanConfiguration, Project } from "@/types";
import { acresToSqyds, sqmToSqft, sqmToSqyd } from "./area";
import { roundToNearest, truncateDecimals } from "./format-number";

function simplifiedName(name: string) {
  const sanitized = name?.replace(/\s/g, "").toUpperCase();
  if (sanitized.match(/2( )?BHK/i)) {
    return "2BHK";
  }
  if (sanitized.match(/3( )?BHK/i)) {
    return "3BHK";
  }
  if (sanitized.match(/4( )?BHK/i)) {
    return "4BHK";
  }
  if (sanitized.match(/5( )?BHK/i)) {
    return "5BHK";
  }
  return sanitized;
}

export function buildFloorMap(buildingDetails: any) {
  const floors = buildingDetails?.reduce((acc: any[], building: any) => {
    return [...building.floors, ...acc];
  }, []);
  const floorMap: Map<string, Set<number>> = floors?.reduce(
    (acc: Map<string, Set<number>>, floor: any) => {
      const { apartmentType, saleableArea, numberOfApartments } = floor;
      if (numberOfApartments == 0 || saleableArea == 0) {
        return acc;
      }
      const perAptArea = roundToNearest(
        truncateDecimals(sqmToSqft(saleableArea / numberOfApartments), 0),
        5
      );
      if (perAptArea > 20000) {
        // remove outliers
        return acc;
      }
      const sanitized = simplifiedName(apartmentType);
      if (acc.has(sanitized)) {
        acc.get(sanitized)?.add(perAptArea);
      } else {
        acc.set(sanitized, new Set([perAptArea]));
      }
      return acc;
    },
    new Map<string, Set<number>>()
  );
  return floorMap;
}

export function isCommonArea(apartmentType: string) {
  return !!apartmentType.match(/(club|amenity|amenities|common).*/i);
}

export function isCommercialArea(apartmentType: string) {
  return !!apartmentType.match(/(commercial|office|retail|shop|shopper).*/i);
}

export function getProjectFloorplanSummary(project: Project) {
  const bedroomsSet = new Set<number>();

  project.floorplanConfigurations?.forEach((fp) => {
    if (fp.rooms && "bedrooms" in fp.rooms) {
      bedroomsSet.add(fp.rooms["bedrooms"]);
    }
  });

  const sorted = project.floorplanConfigurations?.sort(
    (a, b) => a.area - b.area
  );
  return {
    bedrooms: Array.from(bedroomsSet).sort((a, b) => a - b),
    maxArea:
      sorted && sorted.length > 0 ? sorted[sorted.length - 1]?.area : undefined,
    minArea: sorted && sorted.length > 0 ? sorted[0]?.area : undefined,
  };
}

export function getUnitCount(buildingDetails: any) {
  let numberOfApartments = 0;
  buildingDetails.forEach((building: any) => {
    building.floors.forEach((floor: any) => {
      numberOfApartments += floor.numberOfApartments;
    });
  });
  return numberOfApartments;
}

export function calculateUDS(buildingDetails: any, floorplanConfiguration: FloorplanConfiguration, landDetails: any) {
  let totalArea = 0;
  buildingDetails.forEach((building: any) => {
    building.floors.forEach((floor: any) => {
      if (!isCommonArea(floor.apartmentType)) {
        totalArea += floor.saleableArea || 0;
      }
    });
  });
  return sqmToSqyd(floorplanConfiguration.area / totalArea) * (landDetails?.projectAreaNet || 0);
}